import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Input } from '../../../shared/components/ui/Input.js';
import { Modal } from '../../../shared/components/ui/Modal.js';
import { serversApi } from '../api.js';

interface Props {
  open: boolean;
  onClose: () => void;
  serverId: number;
}

export function CreateTenantModal({ open, onClose, serverId }: Props) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminName, setAdminName] = useState('');
  const qc = useQueryClient();

  const reset = (): void => {
    setName('');
    setSlug('');
    setAdminEmail('');
    setAdminPassword('');
    setAdminName('');
  };

  const mut = useMutation({
    mutationFn: async () =>
      serversApi.createTenant(serverId, {
        name,
        slug,
        admin: { email: adminEmail, password: adminPassword, name: adminName },
      }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['tenants', serverId] });
      void qc.invalidateQueries({ queryKey: ['server-health', serverId] });
      toast.success(`Tenant "${data.name}" criado`, {
        description: `Admin "${data.admin.email}" pode logar agora.`,
      });
      reset();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  // Auto-gera slug a partir do nome
  const handleNameChange = (v: string): void => {
    setName(v);
    if (!slug || slug === slugify(name)) {
      setSlug(slugify(v));
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Criar Tenant"
      width={500}
    >
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          mut.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <div className="border-b border-n-200 pb-4">
          <h3 className="mb-3 text-[12px] font-semibold text-n-500 uppercase">Empresa</h3>
          <div className="flex flex-col gap-4">
            <Input
              label="Nome da empresa"
              required
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="ex: Farmácia Boa Saúde"
              autoFocus
            />
            <Input
              label="Slug"
              required
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="farmacia-boa-saude"
              hint="Identificador único — letras minúsculas, números e hífen"
              pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
              minLength={2}
              maxLength={60}
            />
          </div>
        </div>

        <div>
          <h3 className="mb-3 text-[12px] font-semibold text-n-500 uppercase">Primeiro Admin</h3>
          <div className="flex flex-col gap-4">
            <Input
              label="Nome"
              required
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
              placeholder="ex: João Silva"
            />
            <Input
              label="Email"
              type="email"
              required
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="admin@empresa.com"
            />
            <Input
              label="Senha temporária"
              type="text"
              required
              minLength={8}
              maxLength={120}
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder="ao menos 8 caracteres"
              hint="Passe ao cliente — ele troca no primeiro login (futuro)"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mut.isPending}>
            Criar Tenant
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 60);
}
